import { useState } from 'react'
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bug, ExternalLink, RefreshCw } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'
import { useAuth } from '@renderer/chat/providers'
import { MeetingViewSection } from '../nested/section'
import {
  getPlannerDebugRun,
  listPlannerDebugRuns,
  replayPlanner,
  type PlannerDebugRun,
  type PlannerDebugRunDetail,
  type PlannerReplayResult,
  type PlannerReplaySourceReport,
} from './planner-debug-api'
import {
  formatPlannerDebugDate,
  PLANNER_DEBUG_TONE_TEXT,
  PlannerDebugBadge,
  PlannerDebugError,
  type PlannerDebugTone,
} from './planner-debug-shared'
import { PlannerDebugRunDetailView } from './planner-debug-run-detail'

/**
 * Prefix for every query this section owns. The run list lives at
 * `[KEY, noteId]` and each run's detail at `[KEY, noteId, jobId]`, so one
 * prefix invalidation refreshes the list and every open run together.
 */
const RUNS_QUERY_KEY = 'audio-note-planner-debug-runs'
const POLL_INTERVAL_MS = 2000

function isJobActive(status: string): boolean {
  return status === 'queued' || status === 'generating'
}

/**
 * The job row flips to `completed` before the workflow returns its output and
 * while spawned skill chats may still be streaming, so "done" has to come
 * from the detail itself, not the job status alone.
 */
function isDetailInFlight(detail: PlannerDebugRunDetail): boolean {
  return (
    isJobActive(detail.run.status) ||
    detail.scratchpad.status === 'running' ||
    detail.skillRuns.some((skillRun) => skillRun.state === 'running')
  )
}

export function PlannerDebugSection({ noteId }: { noteId: string }) {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)
  const runs = useQuery({
    queryKey: [RUNS_QUERY_KEY, noteId],
    queryFn: () => listPlannerDebugRuns(noteId, getAuthToken),
    enabled: expanded,
    refetchInterval: (query) => (query.state.data?.some((run) => isJobActive(run.status)) ? POLL_INTERVAL_MS : false),
  })
  const fetching = useIsFetching({ queryKey: [RUNS_QUERY_KEY, noteId] }) > 0
  const reload = () => queryClient.invalidateQueries({ queryKey: [RUNS_QUERY_KEY, noteId] })

  return (
    <MeetingViewSection
      icon={<Bug />}
      title="Planner debug"
      expanded={expanded}
      onToggleExpanded={() => setExpanded((value) => !value)}
      trailing={
        expanded ? (
          <Button
            aria-label="Refresh planner runs"
            shape="square"
            variant="ghost"
            loading={fetching}
            onClick={(event) => {
              event.stopPropagation()
              void reload()
            }}
          >
            <RefreshCw />
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        <PlannerReplayPanel
          noteId={noteId}
          onLaunched={(jobId) => {
            setExpandedJobId(jobId)
            void reload()
          }}
        />
        {runs.isLoading && <p className="text-foreground-secondary type-ui-xs">Loading planner runs…</p>}
        {runs.isError && <PlannerDebugError error={runs.error} retry={() => void runs.refetch()} />}
        {runs.data?.length === 0 && (
          <p className="text-foreground-secondary type-ui-xs">No planner runs for this meeting.</p>
        )}
        {runs.data?.map((run) => (
          <PlannerDebugRunRow
            key={run.id}
            noteId={noteId}
            run={run}
            expanded={run.id === expandedJobId}
            onToggle={() => setExpandedJobId((current) => (current === run.id ? null : run.id))}
          />
        ))}
      </div>
    </MeetingViewSection>
  )
}

/**
 * Replay is always two steps: a dry run resolves the anchor trace and reports
 * what each planner source would use, and only that report offers the real
 * launch. There is no way to launch blind.
 */
function PlannerReplayPanel({ noteId, onLaunched }: { noteId: string; onLaunched: (jobId: string) => void }) {
  const { getAuthToken } = useAuth()
  const check = useMutation({ mutationFn: () => replayPlanner(noteId, true, getAuthToken) })
  const launch = useMutation({
    mutationFn: () => replayPlanner(noteId, false, getAuthToken),
    onSuccess: (data) => {
      if (data.launched && data.jobId) onLaunched(data.jobId)
    },
  })
  const report = check.data ?? null
  const canLaunch = report !== null && report.trace !== null && !report.noTrace

  return (
    <div className="corner-smooth flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={report ? 'ghost' : 'outline'}
          loading={check.isPending}
          disabled={check.isPending || launch.isPending}
          onClick={() => {
            launch.reset()
            check.mutate()
          }}
        >
          {report ? 'Re-check replay' : 'Check replay'}
        </Button>
        {canLaunch && (
          <Button
            variant="primary"
            tone="danger"
            loading={launch.isPending}
            disabled={check.isPending || launch.isPending}
            onClick={() => launch.mutate()}
          >
            Launch full replay
          </Button>
        )}
      </div>
      {!report && !check.isPending && (
        <p className="text-foreground-secondary type-ui-xs">
          Checking resolves the anchor trace and shows what each planner source would use, without running anything.
        </p>
      )}
      {check.isError && <PlannerDebugError error={check.error} />}
      {report && <PlannerReplayReportView report={report} />}
      {canLaunch && !launch.data?.launched && (
        <p className="text-yellow-11 type-ui-xs">
          A full replay runs the planner for real and creates real staged actions and reminders for the meeting owner.
        </p>
      )}
      {launch.isError && <PlannerDebugError error={launch.error} />}
      {launch.data?.launched && (
        <p className="text-success-subtle type-ui-xs">
          Replay launched as job {launch.data.jobId}. It is expanded below.
        </p>
      )}
      {launch.data?.refusal && <p className="text-danger-subtle type-ui-xs">Replay refused: {launch.data.refusal}</p>}
    </div>
  )
}

function PlannerReplayReportView({ report }: { report: PlannerReplayResult }) {
  return (
    <div className="type-ui-xs flex flex-col gap-2">
      {report.noTrace && <p className="text-danger-subtle">No replayable trace: {report.noTrace}</p>}
      {report.trace && (
        <a
          href={report.trace.permalink}
          target="_blank"
          rel="noreferrer"
          className="text-foreground-secondary hover:text-foreground flex w-fit items-center gap-1 underline"
        >
          Anchor trace {report.trace.rootSpanId} <ExternalLink className="size-3" />
        </a>
      )}
      {report.sources.length > 0 && <PlannerReplaySourceTable sources={report.sources} preset={report.preset} />}
    </div>
  )
}

const DISPOSITION_TONE: Record<PlannerReplaySourceReport['disposition'], PlannerDebugTone> = {
  recorded: 'good',
  fresh: 'info',
  omit: 'neutral',
}

/**
 * How a source row reads: what it will use (the disposition) and, for recorded
 * sources, whether the trace actually holds a value. A recorded source that
 * cannot be recovered is the row that blocks a launch, so it goes red; a
 * recorded absence replays as absence and is only a warning — except the
 * transcript, without which the backend refuses to launch.
 */
function describeSource(source: PlannerReplaySourceReport): { tone: PlannerDebugTone; detail: string } {
  if (source.disposition === 'fresh') {
    return { tone: 'info', detail: 'Fetched live at replay time, like a normal run.' }
  }
  if (source.disposition === 'omit') {
    return { tone: 'neutral', detail: 'Left out of the prompt.' }
  }
  switch (source.status) {
    case 'found':
      return {
        tone: 'good',
        detail: `Recovered from the anchor trace${source.contentBytes ? ` (${formatBytes(source.contentBytes)})` : ''}.`,
      }
    case 'absent-at-run-time':
      return source.id === 'source.transcript'
        ? { tone: 'bad', detail: 'No transcript was recorded — the replay cannot launch.' }
        : { tone: 'warn', detail: 'Produced nothing in the original run; the replay reproduces the absence.' }
    case 'redacted':
      return { tone: 'bad', detail: 'Redacted in the trace (privacy mode) — the replay cannot launch.' }
    case 'schema-mismatch':
      return { tone: 'bad', detail: `Recorded value no longer parses${source.error ? `: ${source.error}` : '.'}` }
    default:
      return { tone: 'neutral', detail: source.error ?? '' }
  }
}

function formatBytes(bytes: number): string {
  return bytes >= 1024 ? `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB` : `${bytes} B`
}

function PlannerReplaySourceTable({ sources, preset }: { sources: Array<PlannerReplaySourceReport>; preset: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="corner-smooth overflow-x-auto rounded-lg border">
        <table className="type-ui-xs w-full border-collapse text-left">
          <thead>
            <tr className="text-foreground-secondary type-ui-xs uppercase tracking-wide">
              <th className="px-2 py-1.5 font-medium">Source</th>
              <th className="px-2 py-1.5 font-medium">Uses</th>
              <th className="px-2 py-1.5 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const { tone, detail } = describeSource(source)
              return (
                <tr key={source.id} className="border-t">
                  <td className="text-foreground type-code-xs whitespace-nowrap px-2 py-1.5">
                    {source.id.replace('source.', '')}
                  </td>
                  <td className="px-2 py-1.5">
                    <PlannerDebugBadge tone={tone}>{source.disposition}</PlannerDebugBadge>
                  </td>
                  <td className={twMerge('px-2 py-1.5', PLANNER_DEBUG_TONE_TEXT[tone])}>{detail}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-foreground-secondary type-ui-xs">
        Preset “{preset}” · <span className={PLANNER_DEBUG_TONE_TEXT[DISPOSITION_TONE.recorded]}>recorded</span> = from
        the anchor trace · <span className={PLANNER_DEBUG_TONE_TEXT[DISPOSITION_TONE.fresh]}>fresh</span> = fetched now
        · <span className={PLANNER_DEBUG_TONE_TEXT[DISPOSITION_TONE.omit]}>omit</span> = excluded. Red rows block the
        launch.
      </p>
    </div>
  )
}

function PlannerDebugRunRow({
  noteId,
  run: listedRun,
  expanded,
  onToggle,
}: {
  noteId: string
  run: PlannerDebugRun
  expanded: boolean
  onToggle: () => void
}) {
  const { getAuthToken } = useAuth()
  const detail = useQuery({
    queryKey: [RUNS_QUERY_KEY, noteId, listedRun.id],
    queryFn: () => getPlannerDebugRun(noteId, listedRun.id, getAuthToken),
    enabled: expanded,
    refetchInterval: (query) => {
      const data = query.state.data
      const inFlight = data ? isDetailInFlight(data) : isJobActive(listedRun.status)
      return inFlight ? POLL_INTERVAL_MS : false
    },
  })
  // The detail's copy of the run is fresher than the list's once it has loaded
  // (skill chats keep staging actions after the job reads completed).
  const run = detail.data?.run ?? listedRun
  const statusTone = run.status === 'completed' ? 'good' : run.status === 'error' ? 'bad' : 'neutral'

  return (
    <div className="corner-smooth rounded-lg border p-3">
      <button
        type="button"
        className="flex w-full cursor-pointer flex-wrap items-center gap-2 bg-transparent text-left"
        onClick={onToggle}
      >
        <PlannerDebugBadge tone={statusTone}>{run.status}</PlannerDebugBadge>
        {run.isReplay && <PlannerDebugBadge tone="warn">replay</PlannerDebugBadge>}
        <span className="text-foreground-secondary type-ui-xs">
          {run.actionsEmitted} actions · {run.remindersEmitted} reminders · {run.skillChatsSpawned} skills
        </span>
        {(run.toolsets ?? []).map((toolset) => (
          <PlannerDebugBadge key={toolset.name}>
            {toolset.name}
            {toolset.count > 1 ? ` ×${toolset.count}` : ''}
          </PlannerDebugBadge>
        ))}
        <span className="text-foreground-secondary type-ui-xs ml-auto">{formatPlannerDebugDate(run.createdAt)}</span>
      </button>
      {expanded && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="text-foreground-secondary type-code-xs break-all">
            {run.id}
            {run.details ? ` · ${run.details}` : ''}
          </p>
          {run.agentSpans.length > 0 && (
            <div className="flex flex-wrap gap-x-2 gap-y-1">
              {run.agentSpans.map((span) => (
                <a
                  key={span.stagedActionId}
                  href={span.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-foreground-secondary hover:text-foreground type-ui-xs underline"
                >
                  {span.toolSet}/{span.toolName} trace
                </a>
              ))}
            </div>
          )}
          {detail.isLoading && <p className="text-foreground-secondary type-ui-xs">Loading run details…</p>}
          {detail.isError && <PlannerDebugError error={detail.error} retry={() => void detail.refetch()} />}
          {detail.data && <PlannerDebugRunDetailView noteId={noteId} detail={detail.data} />}
        </div>
      )}
    </div>
  )
}
